'use client';
import { type ForwardedRef, type JSX, useCallback, useMemo, useRef, useState } from 'react';

/* eslint-disable-next-line import/order */
import { useForkRef } from '@mui/material';

/* eslint-disable-next-line no-restricted-imports */
import RootSelect, { type SelectProps as RootSelectProps } from '@mui/material/Select';

import { logger } from '../../logger';
import { mergeSx } from '../../util/mui/sx';
import { DefaultInputSize, type InputSize } from '../../util/theme/elements/inputs';
import { useSuspendedPopoverClickAway } from '../floating/hooks/use-suspended-popover-click-away';
import { CaretIcon } from '../icons/CaretIcon';

/**
 * A minimal form of the element that MUI attaches to the 'inputRef' prop for the {@link RootSelect}
 * component that only represents the properties that are of interest to this component.
 *
 * MUI defines the type of the 'inputRef' prop as {@link Ref<any>}, which is not very useful in
 * terms of identifying how the ref can be used.  This type was constructed based on observations
 * of the form of the element that MUI attaches to the underlying ref object, but it is not
 * necessarily guaranteed to always be this case.
 */
type ExpectedMuiSelectInputRef = {
  readonly node: {
    readonly clientWidth: number;
  };
};

/**
 * A typeguard that returns whether or not the provided element, which is attached to the 'inputRef'
 * prop of the {@link RootSelect} component, is in the expected form that this component
 * expects.  This is used to ensure that the component can safely access the 'clientWidth' property
 * of the 'node' property of the element, only if the element is structured as expected.
 *
 * @see {@link ExpectedMuiSelectInputRef}
 */
export const isExpectedMuiSelectInputRef = (
  element: unknown,
): element is ExpectedMuiSelectInputRef => {
  if (
    typeof element === 'object' &&
    element !== null &&
    'node' in element &&
    typeof element.node === 'object' &&
    element.node !== null &&
    'clientWidth' in element.node
  ) {
    return typeof element.node.clientWidth === 'number';
  }
  return false;
};

/**
 * Parses the 'className' property that MUI injects into the callback props for the 'IconComponent'
 * render prop on the {@link Select}. This class name is applied to the custom icon element that
 * is rendered as part of the select component, {@link CaretIcon}, but also as a basis for the
 * determination of whether or not the {@link Select} is currently in an open or closed state at
 * the time that the {@link CaretIcon} is being rendered.
 *
 * Note: At this point in time, after thorough testing, MUI does not seem to include any other
 * information in the 'props' object passed to the 'IconComponent' render prop.  Blindly including
 * a spread of the entire props argument to {@link CaretIcon} would not be safe - so only the
 * class name is funneled through.
 *
 * @param {unknown} props
 *   The callback argument that is passed to the 'IconComponent' render prop, which contains
 *   the 'className' property that needs to be extracted.
 */
const parseIconRenderPropClassName = (props: unknown) => {
  if (
    typeof props === 'object' &&
    props !== null &&
    'className' in props &&
    typeof props.className === 'string'
  ) {
    return props.className;
  }
  return '';
};

export type SelectProps<V = unknown> = {
  /**
   * Defines whether or not the {@link Select} is in a disabled state.  In a disabled state, the
   * {@link Select} popover will not be open.
   *
   * @deprecated
   * This prop is deprecated in favor of the 'isDisabled' prop.
   *
   * @default false
   */
  /* eslint-disable-next-line react/boolean-prop-naming */
  readonly disabled?: boolean;
  /**
   * Defines whether or not the {@link Select} is in a disabled state.  In a disabled state, the
   * {@link Select} popover will not be open.
   *
   * @default false
   */
  readonly isDisabled?: boolean;
  /**
   * Defines whether or not the {@link Select} allows multiple selections.  When true, the popover
   * menu will allow multiple options to be selected at once and the 'value' prop is expected to be
   * an array of values.
   *
   * This prop replaces the 'multiple' prop from MUI's {@link RootSelect} component for purposes
   * of consistency in naming conventions across the codebase, but it serves the same purpose and is
   * passed down to the {@link RootSelect} directly.
   *
   * @default false
   */
  readonly isMulti?: boolean;
  /**
   * Defines whether or not the {@link Select} component's popover menu should be constrained to be
   * exactly the same width as the target element (the input element whose click interactions open
   * and close the popover menu).
   *
   * MUI does not provide a way to constrain the width of the popover menu to the width of the
   * target element directly, so this prop controls whether or not the custom mechanics that
   * apply those width constraints are applied.
   *
   * @default true
   */
  readonly isPopoverWidthConstrainedToTarget?: boolean;
  /**
   * Defines the size of the {@link Select} component and its associated size-related properties,
   * such as inner padding, height, font sizes, line heights, etc.  The size is applied via an
   * HTML data attribute so the theme can apply size-specific styling.
   *
   * This size prop is an internal definition that should be uniform across all inputs in the
   * application - it does not map to MUI's 'size' for the {@link Select} 1-1:
   *
   * | Input Size | MUI Input Size |
   * | ---------- | -------------- |
   * | small      | (none)         |
   * | medium     | small          |
   * | large      | medium         |
   *
   * Our 'small' size is an extension that does not have a MUI counterpart.
   *
   * @see {@link InputSize}
   *
   * @default 'medium'
   */
  readonly size?: `${InputSize}`;
} & Omit<RootSelectProps<V>, 'disabled' | 'multiple' | 'open' | 'size'>;

type SelectWidth =
  | { readonly isInitialized: false; readonly value?: never }
  | { readonly isInitialized: true; readonly value: number };

/**
 * Prevents MUI from applying transformations to the CaretIcon that the CaretIcon applies via
 * framer-motion. Omitting this would cause the CaretIcon to flicker and redundantly transform back
 * to its original direction on any open/close event. */
const PreventRedundantIconRotationSx = {
  '& .MuiSelect-iconOpen': {
    transform: 'none',
  },
};

/**
 * An extension of MUI's {@link RootSelect} component that internalizes select-related logic
 * specific to the application's usage of selects in order to provide a single, consistent
 * implementation of selects across the application.
 *
 * This Select introduces a few key behaviors:
 *
 * 1. Target Width Constraint
 *    The {@link Select} exposes a {@link SelectProps.isPopoverWidthConstrainedToTarget} prop that,
 *    when true, constrains the width of the Select's popover menu to be the same width as the
 *    {@link Select}'s target/trigger element.
 *
 *    This adjustment is made imperatively at the time the {@link Select}'s menu is opened to
 *    avoid usage of side-effects and redundant rendering.
 *
 * 2. Coordination with Enclosing Popover
 *    When a {@link Select} is used within an enclosing {@link Popover}, the click-away listener of
 *    the enclosing {@link Popover} can interfere with interactions with the {@link Select}'s
 *    popover menu, which is portaled and can result in clicks on the menu being registered as
 *    clicks outside of the enclosing {@link Popover} and causing it to close unexpectedly.
 *
 *    This is a known issue with MUI's popover/select behavior (see
 *    https://github.com/mui/material-ui/issues/25578).
 *
 *    This {@link Select} component is aware of whether or not it is being rendered within a
 *    {@link Popover} via the {@link useSuspendedPopoverClickAway} hook and, when the select menu
 *    is open, it automatically suspends the enclosing {@link Popover}'s click-away listener.
 *
 *    This also allows the {@link Select} to remain open when {@link SelectProps.isMulti} is true
 *    and a user selects an option.
 */
export const Select = <V = unknown,>({
  children,
  isDisabled,
  isMulti,
  isPopoverWidthConstrainedToTarget = true,
  onClose,
  onOpen,
  ref,
  size = DefaultInputSize,
  ...props
}: { readonly ref?: ForwardedRef<HTMLDivElement> } & SelectProps<V>): JSX.Element => {
  const _inputRef = useRef<unknown>(null);
  const [selectWidth, setSelectWidth] = useState<SelectWidth>({ isInitialized: false });

  const inputRef = useForkRef(props.inputRef, _inputRef);

  const disabled = isDisabled ?? props.disabled ?? false;

  const { isInPopoverScope, resume, suspend } = useSuspendedPopoverClickAway();

  const constrainWidthToTarget = useCallback(() => {
    if (_inputRef.current && isPopoverWidthConstrainedToTarget) {
      if (!isExpectedMuiSelectInputRef(_inputRef.current)) {
        logger.warn(
          "Expected the element attached to the Select's 'inputRef' to conform to a specific " +
            "shape when it does not.  The width of the Select's popover will not be " +
            "constrained to the target element's width.",
        );
        return;
      }
      setSelectWidth({ isInitialized: true, value: _inputRef.current.node.clientWidth });
    }
  }, [isPopoverWidthConstrainedToTarget]);

  const propMaxWidth = props.MenuProps?.PaperProps?.style?.maxWidth;
  const propMinWidth = props.MenuProps?.PaperProps?.style?.minWidth;

  const widthConstraint = useMemo(() => {
    if (isPopoverWidthConstrainedToTarget && selectWidth.isInitialized) {
      return {
        maxWidth: propMaxWidth ?? selectWidth.value,
        minWidth: propMinWidth ?? selectWidth.value,
      };
    }
    return {
      maxWidth: propMaxWidth,
      minWidth: propMinWidth,
    };
  }, [isPopoverWidthConstrainedToTarget, selectWidth, propMaxWidth, propMinWidth]);

  return (
    <RootSelect<V>
      {...props}
      data-attr-size={size}
      disabled={disabled}
      IconComponent={(iconProps: unknown) => {
        const className = parseIconRenderPropClassName(iconProps);
        return (
          <CaretIcon
            className={className}
            data-testid='select-caret-icon'
            direction={className.includes('MuiSelect-iconOpen') ? 'up' : 'down'}
            size={20}
            sx={{ top: 0 }}
          />
        );
      }}
      inputRef={inputRef}
      MenuProps={{
        ...props.MenuProps,
        MenuListProps: {
          ...props.MenuProps?.MenuListProps,
          sx: mergeSx({ paddingY: 0 }, props.MenuProps?.MenuListProps?.sx),
        },
        PaperProps: {
          ...props.MenuProps?.PaperProps,
          style: {
            ...props.MenuProps?.PaperProps?.style,
            ...widthConstraint,
          },
        },
        style: { transform: 'translateY(4px)', ...props.MenuProps?.style },
      }}
      multiple={isMulti}
      onClose={event => {
        if (isInPopoverScope) {
          resume();
        }
        onClose?.(event);
      }}
      onOpen={event => {
        if (isInPopoverScope) {
          suspend();
        }
        constrainWidthToTarget();
        onOpen?.(event);
      }}
      ref={ref}
      sx={mergeSx(props.sx, PreventRedundantIconRotationSx)}
    >
      {children}
    </RootSelect>
  );
};
